import { useState } from 'react';
import { Card, Badge, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { MODULE_IDS, MODULE_LABELS } from '@mirai/shared';
import { useRole } from '@/hooks/useRole';
import styles from './Plans.module.css';

const formatCurrency = (v: number) => `¥${v.toLocaleString('ja-JP')}`;

interface Plan {
  id: number; name: string; price_monthly: number; max_users: number;
  max_properties: number; max_storage_gb: number; modules: string[]; tenant_count: number;
}

interface Tenant {
  id: string; slug: string; company_name: string; plan_name: string; status: string; modules?: string[];
}

interface PlanFeature {
  plan_id: number; feature_text: string; enabled: boolean; sort_order: number;
}

interface Addon {
  id: string; icon: string; name: string; description: string; price: number; price_label: string; note: string;
}

export function Plans() {
  const { isSuperAdmin } = useRole();
  const { data: plans } = useApi<Plan[]>('/console/plans');
  const { data: tenants } = useApi<Tenant[]>('/console/tenants');
  const { data: featureRows } = useApi<PlanFeature[]>('/console/plan-features');
  const { data: addonDefs } = useApi<Addon[]>('/console/addons');

  // featureRowsをプラン名別にグループ化（重複排除）
  const planFeatures: Record<string, { features: { text: string; on: boolean }[] }> = {};
  if (featureRows && plans) {
    for (const plan of plans) {
      const rawFeatures = (featureRows ?? []).filter((f) => f.plan_id === plan.id).sort((a, b) => a.sort_order - b.sort_order);
      // feature_text で重複排除
      const seen = new Set<string>();
      const unique = rawFeatures.filter((f) => {
        if (seen.has(f.feature_text)) return false;
        seen.add(f.feature_text);
        return true;
      });
      planFeatures[plan.name] = { features: unique.map((f) => ({ text: f.feature_text, on: f.enabled })) };
    }
  }
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', price_monthly: 0, max_users: 3, max_properties: 100, max_storage_gb: 10, modules: [] as string[] });
  const [editPlan, setEditPlan] = useState<Plan | null>(null);
  const [editModules, setEditModules] = useState<string[]>([]);
  const [addonForm, setAddonForm] = useState({ tenantId: '', selectedAddons: [] as string[] });

  const displayPlans = (plans ?? []).filter((p) => p.name !== 'Trial');
  const activeTenants = (tenants ?? []).filter((t) => t.status === 'active' || t.status === 'trial');

  const toggleAddon = (id: string) => {
    setAddonForm((prev) => ({
      ...prev,
      selectedAddons: prev.selectedAddons.includes(id)
        ? prev.selectedAddons.filter((a) => a !== id)
        : [...prev.selectedAddons, id],
    }));
  };

  const totalAddonPrice = addonForm.selectedAddons.reduce((sum, id) => {
    const addon = (addonDefs ?? []).find((a) => a.id === id);
    return sum + (addon?.price ?? 0);
  }, 0);

  const selectedTenantName = activeTenants.find((t) => t.id === addonForm.tenantId)?.company_name;

  const handleAddAddon = async () => {
    if (!addonForm.tenantId) { toast('テナントを選択してください', 'error'); return; }
    if (addonForm.selectedAddons.length === 0) { toast('アドオンを選択してください', 'error'); return; }
    try {
      const tenant = activeTenants.find((t) => t.id === addonForm.tenantId);
      const currentModules = (tenant as { modules?: string[] })?.modules ?? [];
      const newModules = [...new Set([...currentModules, ...addonForm.selectedAddons])];
      await api(`/console/tenants/${addonForm.tenantId}`, { method: 'PATCH', body: JSON.stringify({ enabled_modules: newModules }) });
      const names = addonForm.selectedAddons.map((id) => (addonDefs ?? []).find((a) => a.id === id)?.name).join('、');
      toast(`✓ ${selectedTenantName} に ${names} を追加しました。次回請求から反映されます`);
      setShowAddonModal(false);
      setAddonForm({ tenantId: '', selectedAddons: [] });
    } catch { toast('アドオン追加に失敗しました', 'error'); }
  };

  const openEditPlan = (plan: Plan) => {
    setEditPlan(plan);
    setEditModules([...(plan.modules || [])]);
    setShowEditModal(true);
  };

  const toggleEditModule = (mod: string) => {
    setEditModules((prev) => prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]);
  };

  interface HistoryItem { id: number; tenant_name: string; change_description: string; changed_by: string; created_at: string; }
  const { data: historyFromDb } = useApi<HistoryItem[]>('/console/plan-history');
  const historyData = (historyFromDb ?? []).map((h) => ({
    date: new Date(h.created_at).toLocaleDateString('ja-JP'),
    tenant: h.tenant_name,
    change: h.change_description,
    by: h.changed_by,
  }));

  const allModules = MODULE_IDS;
  const moduleLabels = MODULE_LABELS;

  return (
    <div className={styles.page}>
      <div className={styles.topActions}>
        <button className={styles.btnSecondary} onClick={() => setShowHistoryModal(true)}>📋 変更履歴</button>
        {isSuperAdmin && <button className={styles.btnSecondary} onClick={() => { if (displayPlans[0]) openEditPlan(displayPlans[0]); }}>✏️ プラン編集</button>}
        {isSuperAdmin && <button className={styles.btnPrimary} onClick={() => setShowCreateModal(true)}>＋ プラン追加</button>}
      </div>

      <div className={styles.planGrid}>
        {displayPlans.map((plan) => {
          const feats = planFeatures[plan.name];
          const isFeatured = plan.name === 'Professional';
          return (
            <div key={plan.id} className={`${styles.planCard} ${isFeatured ? styles.featured : ''}`}>
              <div className={styles.planHeader}>
                <div className={styles.planName}>{plan.name}</div>
                {isFeatured && <Badge variant="blue">人気</Badge>}
              </div>
              <div className={styles.planPrice}>
                {formatCurrency(plan.price_monthly)}<span className={styles.planPriceUnit}>{plan.name === 'Enterprise' ? '/月〜' : '/月'}</span>
              </div>
              <div className={styles.divider} />
              <div className={styles.planCount}>
                {plan.tenant_count} <span className={styles.planCountLabel}>テナント契約中</span>
              </div>
              <div className={styles.divider} />
              {feats?.features.map((f, i) => (
                <div key={i} className={`${styles.feat} ${!f.on ? styles.off : ''}`}>{f.text}</div>
              ))}
            </div>
          );
        })}
      </div>

      <Card title="🔌 アドオンモジュール" actions={
        <button className={styles.btnPrimary} onClick={() => setShowAddonModal(true)}>＋ アドオン追加</button>
      }>
        {(addonDefs ?? []).map((a) => (
          <div key={a.id} className={styles.modRow}>
            <div className={styles.modIcon}>{a.icon}</div>
            <div className={styles.modInfo}>
              <div className={styles.modName}>{a.name}</div>
              <div className={styles.modDesc}>{a.description}</div>
            </div>
            <Badge variant="amber">{a.price_label}</Badge>
            {a.note && <Badge variant="gray">{a.note}</Badge>}
          </div>
        ))}
      </Card>

      {/* アドオン追加モーダル */}
      <Modal open={showAddonModal} onClose={() => setShowAddonModal(false)} title="🔌 アドオン追加" width={540}
        footer={<>
          <button className={styles.btnGhost} onClick={() => setShowAddonModal(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleAddAddon}>アドオンを適用</button>
        </>}
      >
        {/* テナント選択 */}
        <div className={styles.formSection}>対象テナント</div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>テナント</label>
          <select className={styles.formSelect} value={addonForm.tenantId} onChange={(e) => setAddonForm({ ...addonForm, tenantId: e.target.value })}>
            <option value="">テナントを選択...</option>
            {activeTenants.map((t) => (
              <option key={t.id} value={t.id}>{t.company_name}（{t.plan_name}）</option>
            ))}
          </select>
        </div>

        {/* アドオン選択 */}
        <div className={styles.formSection}>アドオンモジュール選択</div>
        <div className={styles.addonList}>
          {(addonDefs ?? []).map((a) => (
            <label key={a.id} className={`${styles.addonItem} ${addonForm.selectedAddons.includes(a.id) ? styles.addonSelected : ''}`}>
              <input
                type="checkbox"
                checked={addonForm.selectedAddons.includes(a.id)}
                onChange={() => toggleAddon(a.id)}
                className={styles.addonCheckbox}
              />
              <div className={styles.addonIcon}>{a.icon}</div>
              <div className={styles.addonInfo}>
                <div className={styles.addonName}>{a.name}</div>
                <div className={styles.addonDesc}>{a.description}</div>
              </div>
              <div className={styles.addonPrice}>{a.price_label}</div>
            </label>
          ))}
        </div>

        {/* 確認 */}
        {addonForm.selectedAddons.length > 0 && (
          <div className={styles.addonSummary}>
            <div className={styles.addonSummaryRow}>
              <span>選択アドオン数</span>
              <span style={{ fontWeight: 700, fontFamily: 'var(--mono)' }}>{addonForm.selectedAddons.length} 件</span>
            </div>
            <div className={styles.addonSummaryRow}>
              <span>追加月額</span>
              <span style={{ fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--brand)' }}>+{formatCurrency(totalAddonPrice)}/月</span>
            </div>
          </div>
        )}

        <div className={styles.infoBanner}>
          ✓ アドオンは即時有効化され、次回請求から日割り計算で加算されます。
        </div>
      </Modal>

      {/* 変更履歴モーダル */}
      <Modal open={showHistoryModal} onClose={() => setShowHistoryModal(false)} title="📋 プラン・アドオン変更履歴" width={640}>
        <table className={styles.historyTable}>
          <thead>
            <tr>
              <th className={styles.historyTh}>日付</th>
              <th className={styles.historyTh}>テナント</th>
              <th className={styles.historyTh}>変更内容</th>
              <th className={styles.historyTh}>実行者</th>
            </tr>
          </thead>
          <tbody>
            {historyData.length === 0 ? (
              <tr><td colSpan={4} className={styles.historyTd} style={{ textAlign: 'center', color: 'var(--ink3)', padding: 24 }}>変更履歴はありません</td></tr>
            ) : historyData.map((h, i) => (
              <tr key={i}>
                <td className={styles.historyTd} style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{h.date}</td>
                <td className={styles.historyTd} style={{ fontWeight: 600 }}>{h.tenant}</td>
                <td className={styles.historyTd}>{h.change}</td>
                <td className={styles.historyTd} style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink3)' }}>{h.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>

      {/* プラン編集モーダル */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} title="✏️ プラン編集" width={540}
        footer={<>
          <button className={styles.btnGhost} onClick={() => setShowEditModal(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={async () => {
            if (!editPlan) return;
            try {
              await api(`/console/plans/${editPlan.id}`, { method: 'PATCH', body: JSON.stringify({ modules: editModules }) });
              toast(`✓ ${editPlan.name} プランを更新しました`);
              setShowEditModal(false);
            } catch { toast('プラン更新に失敗しました', 'error'); }
          }}>変更を保存</button>
        </>}
      >
        {editPlan && (
          <div className={styles.planEditGrid}>
            <div className={styles.formSection}>プラン選択</div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>編集対象プラン</label>
              <select className={styles.formSelect} value={editPlan.name} onChange={(e) => {
                const p = displayPlans.find((pl) => pl.name === e.target.value);
                if (p) openEditPlan(p);
              }}>
                {displayPlans.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>

            <div className={styles.formSection}>基本情報</div>
            <div className={styles.planEditRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>月額料金（税抜）</label>
                <input className={styles.formInput} type="number" defaultValue={editPlan.price_monthly} style={{ fontFamily: 'var(--mono)' }} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>ユーザー上限</label>
                <input className={styles.formInput} type="number" defaultValue={editPlan.max_users} style={{ fontFamily: 'var(--mono)' }} />
              </div>
            </div>
            <div className={styles.planEditRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>物件上限</label>
                <input className={styles.formInput} type="number" defaultValue={editPlan.max_properties} style={{ fontFamily: 'var(--mono)' }} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>ストレージ（GB）</label>
                <input className={styles.formInput} type="number" defaultValue={editPlan.max_storage_gb} style={{ fontFamily: 'var(--mono)' }} />
              </div>
            </div>

            <div className={styles.formSection}>含まれるモジュール</div>
            <div className={styles.formGroup}>
              <div className={styles.formChecks}>
                {allModules.map((mod) => (
                  <label key={mod} className={styles.formCheck}>
                    <input type="checkbox" checked={editModules.includes(mod)} onChange={() => toggleEditModule(mod)} />
                    {moduleLabels[mod] ?? mod}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.infoBanner}>
              ⚠ プラン変更は既存テナント（{editPlan.tenant_count}社）に即時反映されません。個別にプラン移行が必要です。
            </div>
          </div>
        )}
      </Modal>
      {/* プラン新規作成モーダル */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="＋ プラン新規作成" width={540}
        footer={<>
          <button className={styles.btnGhost} onClick={() => setShowCreateModal(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={async () => {
            if (!createForm.name.trim()) { toast('プラン名は必須です', 'error'); return; }
            try {
              await api('/console/plans', { method: 'POST', body: JSON.stringify(createForm) });
              toast(`✓ ${createForm.name} プランを作成しました`);
              setShowCreateModal(false);
              setCreateForm({ name: '', price_monthly: 0, max_users: 3, max_properties: 100, max_storage_gb: 10, modules: [] });
              window.location.reload();
            } catch (e) { toast(e instanceof Error ? e.message : 'プラン作成に失敗しました', 'error'); }
          }}>プランを作成</button>
        </>}
      >
        <div className={styles.planEditGrid}>
          <div className={styles.formSection}>基本情報</div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>プラン名 *</label>
            <input className={styles.formInput} value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="例: Business" />
          </div>
          <div className={styles.planEditRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>月額料金（税抜）</label>
              <input className={styles.formInput} type="number" value={createForm.price_monthly} onChange={(e) => setCreateForm({ ...createForm, price_monthly: Number(e.target.value) })} style={{ fontFamily: 'var(--mono)' }} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>ユーザー上限</label>
              <input className={styles.formInput} type="number" value={createForm.max_users} onChange={(e) => setCreateForm({ ...createForm, max_users: Number(e.target.value) })} style={{ fontFamily: 'var(--mono)' }} />
            </div>
          </div>
          <div className={styles.planEditRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>物件上限</label>
              <input className={styles.formInput} type="number" value={createForm.max_properties} onChange={(e) => setCreateForm({ ...createForm, max_properties: Number(e.target.value) })} style={{ fontFamily: 'var(--mono)' }} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>ストレージ（GB）</label>
              <input className={styles.formInput} type="number" value={createForm.max_storage_gb} onChange={(e) => setCreateForm({ ...createForm, max_storage_gb: Number(e.target.value) })} style={{ fontFamily: 'var(--mono)' }} />
            </div>
          </div>

          <div className={styles.formSection}>含まれるモジュール</div>
          <div className={styles.formGroup}>
            <div className={styles.formChecks}>
              {allModules.map((mod) => (
                <label key={mod} className={styles.formCheck}>
                  <input type="checkbox" checked={createForm.modules.includes(mod)} onChange={() => setCreateForm((prev) => ({
                    ...prev,
                    modules: prev.modules.includes(mod) ? prev.modules.filter((m) => m !== mod) : [...prev.modules, mod],
                  }))} />
                  {moduleLabels[mod] ?? mod}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
